/**
 * RoleHome — role-aware home page for a nationwide QSR. A staff/role switcher
 * (Owner, State Manager, Area Manager, Store Manager, Operations, Finance,
 * Marketing, People/HR, Shift Supervisor) re-skins the greeting, the "today at a
 * glance" strip and the focus cards so every staff member sees what matters to
 * THEIR job. Store/State roles get a second scope selector.
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Crown, Map, Layers, Store, Gauge, Calculator, Megaphone, Users, Radio,
  DollarSign, Timer, Trash2, Bike, TrendingUp, ArrowRight, Sparkles, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import KpiTile from './KpiTile';
import Sparkline from './Sparkline';
import AskCorvana from './AskCorvana';
import { QSR_STORES, TARGETS, avgBy, money } from './qsrDemoData';
import { LivePulse } from '@/components/demo/LivePulse';
import { demoBump } from '@/components/demo/demoBus';

// Live "streaming now" strip inside the hero — figures tick with a gentle drift
// AND jump each time a live order toast fires (window 'qsr-live-order' event).
function QsrLiveStrip({ glow, scope, store }) {
  const isStore = scope !== 'network';
  const baseSales = isStore ? (store?.weeklySales || 72000) / 7 : QSR_STORES.reduce((s, x) => s + x.weeklySales, 0) / 7;
  const baseOrders = Math.round(baseSales / 18.6);
  const baseDelivery = Math.round(baseOrders * (isStore ? (store?.delivery?.mix || 28) : 28) / 100);
  // Include any payoff already banked (offer launched on another tab) so the win persists.
  const seedSales = baseSales + (isStore ? 0 : demoBump.sales);
  const seedOrders = baseOrders + (isStore ? 0 : demoBump.orders);
  const ref = useRef({ sales: seedSales, orders: seedOrders, delivery: baseDelivery });
  const [vals, setVals] = useState({ sales: seedSales, orders: seedOrders, delivery: baseDelivery });
  const [bump, setBump] = useState(false);
  const [payoff, setPayoff] = useState(false);
  const bumpTimer = useRef(null);
  // Keep current scope/store fresh for the (mount-only) order listener.
  const scopeRef = useRef({ scope, storeId: store?.id });
  useEffect(() => { scopeRef.current = { scope, storeId: store?.id }; }, [scope, store]);

  // Re-baseline when scope/store changes (carry the banked payoff on network view).
  useEffect(() => {
    const s = baseSales + (isStore ? 0 : demoBump.sales);
    const o = baseOrders + (isStore ? 0 : demoBump.orders);
    ref.current = { sales: s, orders: o, delivery: baseDelivery };
    setVals({ ...ref.current });
  }, [baseSales, baseOrders, baseDelivery, isStore]);

  // Gentle upward drift so the numbers are always alive.
  useEffect(() => {
    const id = setInterval(() => {
      ref.current.sales += baseSales * (0.0012 + Math.random() * 0.0012);
      ref.current.orders += Math.random() < 0.6 ? 1 : 0;
      setVals({ ...ref.current });
    }, 2600);
    return () => clearInterval(id);
  }, [baseSales]);

  // Each live order toast bumps the counters so they move together.
  // In a store-scoped view, only count orders for THAT store.
  useEffect(() => {
    const onOrder = (e) => {
      const { scope: sc, storeId: sid } = scopeRef.current;
      if (sc !== 'network' && e.detail?.storeId !== sid) return;
      const amt = Number(e.detail?.amount) || 0;
      ref.current.orders += 1;
      ref.current.sales += amt;
      if (e.detail?.delivery) ref.current.delivery += 1;
      setVals({ ...ref.current });
      setBump(true);
      if (bumpTimer.current) clearTimeout(bumpTimer.current);
      bumpTimer.current = setTimeout(() => setBump(false), 700);
    };
    window.addEventListener('qsr-live-order', onOrder);
    return () => { window.removeEventListener('qsr-live-order', onOrder); if (bumpTimer.current) clearTimeout(bumpTimer.current); };
  }, []);

  // Loyalty-offer payoff — a bigger, celebratory step-up when the offer launches.
  useEffect(() => {
    const onPayoff = (e) => {
      if (scopeRef.current.scope !== 'network') return; // banked figure is network-wide
      ref.current.orders += Number(e.detail?.orders) || 0;
      ref.current.sales += Number(e.detail?.sales) || 0;
      setVals({ ...ref.current });
      setPayoff(true);
      setTimeout(() => setPayoff(false), 2000);
    };
    window.addEventListener('qsr-payoff-bump', onPayoff);
    return () => window.removeEventListener('qsr-payoff-bump', onPayoff);
  }, []);

  const avg = vals.sales / Math.max(1, vals.orders);
  const tiles = [
    { label: 'Sales today', value: `$${Math.round(vals.sales).toLocaleString()}`, testid: 'hero-live-sales', live: true },
    { label: 'Orders today', value: Math.round(vals.orders).toLocaleString(), testid: 'hero-live-orders', live: true },
    { label: 'Avg check', value: `$${avg.toFixed(2)}`, testid: 'hero-live-avgcheck', live: false },
    { label: 'Delivery orders', value: Math.round(vals.delivery).toLocaleString(), testid: 'hero-live-delivery', live: false },
  ];
  return (
    <div className="mt-4 rounded-2xl bg-white/5 ring-1 ring-white/10 p-3 backdrop-blur" data-testid="hero-live-strip">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-white/60">{isStore ? `${store?.name} — streaming now` : 'Network — streaming now'}</span>
        <LivePulse sources={['Square', 'Toast', 'Lightspeed', 'Uber Eats', 'DoorDash']} className="!bg-white/10 !text-emerald-300 !border-white/10" />
      </div>
      <div className="grid keep-cols grid-cols-2 sm:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <div key={t.testid}>
            <p className="text-[11px] text-white/50">{t.label}</p>
            <p
              className={`text-xl font-bold tabular-nums transition-all duration-300 origin-left ${payoff && t.live ? 'scale-125 drop-shadow-[0_0_10px_rgba(16,185,129,0.7)]' : bump && t.live ? 'scale-110' : 'scale-100'}`}
              style={{ color: payoff && t.live ? '#10b981' : glow }}
              data-testid={t.testid}
            >
              {t.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

const STATE_OF = { footscray: 'VIC', penrith: 'NSW', southport: 'QLD', joondalup: 'WA', prospect: 'SA' };

const ROLES = [
  { id: 'owner', label: 'Owner / CEO', icon: Crown, persona: 'Karen', scope: 'network' },
  { id: 'state', label: 'State Manager', icon: Map, persona: 'Dylan', scope: 'state' },
  { id: 'area', label: 'Area Manager', icon: Layers, persona: 'Priya', scope: 'network' },
  { id: 'store', label: 'Store Manager', icon: Store, persona: 'Rhys', scope: 'store' },
  { id: 'ops', label: 'Operations Manager', icon: Gauge, persona: 'Marco', scope: 'network' },
  { id: 'finance', label: 'Accountant / Finance', icon: Calculator, persona: 'Wei', scope: 'network' },
  { id: 'marketing', label: 'Marketing Manager', icon: Megaphone, persona: 'Sofia', scope: 'network' },
  { id: 'people', label: 'People / HR', icon: Users, persona: 'Aisha', scope: 'network' },
  { id: 'supervisor', label: 'Shift Supervisor', icon: Radio, persona: 'Jordan', scope: 'store' },
];

export default function RoleHome({ onNavigate, onSelectStore, roleId: roleIdProp, storeId: storeIdProp, onRoleChange, onStoreChange }) {
  const [roleIdLocal, setRoleIdLocal] = useState('owner');
  const [storeIdLocal, setStoreIdLocal] = useState('southport');
  const [momentumPeriod, setMomentumPeriod] = useState('day');
  const roleId = roleIdProp ?? roleIdLocal;
  const storeId = storeIdProp ?? storeIdLocal;
  const setRoleId = onRoleChange || setRoleIdLocal;
  const setStoreId = onStoreChange || setStoreIdLocal;
  const role = ROLES.find((r) => r.id === roleId);

  const agg = useMemo(() => {
    const netSales = QSR_STORES.reduce((s, x) => s + x.weeklySales, 0);
    const splh = avgBy('splh'); const sos = avgBy('sos'); const acc = avgBy('accuracy');
    const labour = avgBy('labourPct'); const waste = avgBy('wastePct');
    const delivery = QSR_STORES.reduce((s, x) => s + x.delivery.mix, 0) / QSR_STORES.length;
    const ranked = [...QSR_STORES].sort((a, b) => b.splh - a.splh);
    return { netSales, splh, sos, acc, labour, waste, delivery, best: ranked[0], worst: ranked[ranked.length - 1] };
  }, []);

  const store = QSR_STORES.find((s) => s.id === storeId) || QSR_STORES[0];
  const view = buildView(role, agg, store);
  const momentum = momentumFor(role, store, momentumPeriod);
  const glanceSource = role.scope === 'network'
    ? (role.id === 'finance' ? 'Lightspeed POS + Tanda · ATO benchmarks'
      : role.id === 'marketing' ? 'Lightspeed POS + Doshii'
      : role.id === 'people' ? 'Tanda roster'
      : role.id === 'ops' ? 'Lightspeed KDS · QSR benchmarks'
      : 'Lightspeed + Tanda · network roll-up')
    : `${store.name} · Lightspeed + Tanda`;

  return (
    <div className="qsr-stage p-3 sm:p-5 space-y-4" data-testid="role-home">
      {/* Role switcher */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Home for</span>
          <Select value={roleId} onValueChange={setRoleId}>
            <SelectTrigger className="w-[210px] h-9 rounded-full bg-white/80 border-slate-200 font-semibold text-sm" data-testid="role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => (
                <SelectItem key={r.id} value={r.id} data-testid={`role-opt-${r.id}`}>
                  <span className="inline-flex items-center gap-2"><r.icon className="w-3.5 h-3.5" /> {r.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(role.scope === 'store' || role.scope === 'state') && (
            <Select value={storeId} onValueChange={setStoreId}>
              <SelectTrigger className="w-[190px] h-9 rounded-full bg-white/80 border-slate-200 font-semibold text-sm" data-testid="scope-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QSR_STORES.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`scope-opt-${s.id}`}>
                    {role.scope === 'state' ? `${STATE_OF[s.id]} · ${s.name}` : s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Persona hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 to-slate-700 text-white p-5 sm:p-6 shadow-2xl ring-1 ring-white/10" data-testid="role-hero">
        <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full blur-3xl pointer-events-none" style={{ background: `${view.glow}33` }} />
        <div className="relative">
          <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: view.glow }}>
            <role.icon className="w-4 h-4" /> {role.label}{view.scopeLabel ? ` · ${view.scopeLabel}` : ''}
          </div>
          <h2 className="text-2xl font-semibold tracking-tight mt-1">{role.scope === 'network' ? 'Network command centre' : `${store.name} store`}</h2>
          <p className="text-sm text-slate-300 mt-1">{view.tagline}</p>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-sm font-semibold backdrop-blur">
            <Sparkles className="w-4 h-4" style={{ color: view.glow }} /> {view.headline}
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="role-momentum">
            <div className="inline-flex items-center rounded-full bg-white/10 p-0.5 backdrop-blur">
              <button onClick={() => setMomentumPeriod('day')} className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors ${momentumPeriod === 'day' ? 'bg-white text-slate-900' : 'text-white/70 hover:text-white'}`} data-testid="momentum-period-day">Yesterday</button>
              <button onClick={() => setMomentumPeriod('week')} className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors ${momentumPeriod === 'week' ? 'bg-white text-slate-900' : 'text-white/70 hover:text-white'}`} data-testid="momentum-period-week">Last week</button>
            </div>
            {momentum.map((m, i) => (
              <span key={`${roleId}-${momentumPeriod}-${i}`} className={`inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-2 py-1 text-xs font-semibold ${m.good ? 'bg-emerald-400/15 text-emerald-300' : 'bg-rose-400/20 text-rose-200'}`} data-testid={`momentum-${i}`}>
                {m.good ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}{m.text}
                <Sparkline data={m.good ? [3, 3.3, 3.1, 3.7, 4.0, 4.3, 4.7] : [4.7, 4.3, 4.0, 3.7, 3.1, 3.3, 3]} color={m.good ? '#34d399' : '#fda4af'} width={38} height={14} strokeWidth={1.75} className="opacity-90" />
              </span>
            ))}
          </div>
          <QsrLiveStrip glow={view.glow} scope={role.scope} store={store} />
        </div>
      </div>

      {/* Today at a glance */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-900">Today at a glance <span className="ml-1 text-[11px] font-normal text-slate-500">· tap any tile to drill in</span></h3>
          <AskCorvana insight={view.ai} />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="role-glance">
          {view.glance.map((g, i) => (
            <div
              key={i}
              role="button"
              tabIndex={0}
              onClick={() => { if (role.scope !== 'network') onSelectStore?.(store); else onNavigate?.(g.tab || 'locations'); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { if (role.scope !== 'network') onSelectStore?.(store); else onNavigate?.(g.tab || 'locations'); } }}
              className="cursor-pointer transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-slate-300 rounded-2xl"
              data-testid={`glance-${i}`}
              title={role.scope !== 'network' ? `Open ${store.name} detail` : 'View locations'}
            >
              <KpiTile icon={g.icon} label={g.label} value={g.value} sub={g.sub} color={g.color} trend={g.trend} delta={g.delta} deltaGood={g.deltaGood} source={g.source || glanceSource} />
            </div>
          ))}
        </div>
      </div>

      {/* Role focus cards */}
      <div className="grid sm:grid-cols-3 gap-3" data-testid="role-focus">
        {view.focus.map((f, i) => (
          <button
            key={i}
            onClick={() => { if (f.store) onSelectStore?.(f.store); else onNavigate?.(f.tab); }}
            className="qsr-card rounded-2xl p-4 text-left group"
            data-testid={`focus-${i}`}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span className="p-1.5 rounded-lg" style={{ background: `${f.color}1a`, color: f.color }}><f.icon className="w-4 h-4" /></span>
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{f.kicker}</span>
            </div>
            <div className="font-semibold text-sm text-slate-900">{f.title}</div>
            <p className="text-xs text-slate-500 mt-0.5">{f.body}</p>
            <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-slate-700 group-hover:gap-1.5 transition-all">{f.cta} <ArrowRight className="w-3.5 h-3.5" /></span>
          </button>
        ))}
      </div>
    </div>
  );
}

function momentumFor(role, store, period = 'day') {
  const w = period === 'week';
  switch (role.id) {
    case 'owner':
    case 'area':
      return w
        ? [{ text: 'SPLH +$3.40', good: true }, { text: 'Speed −0.6m', good: true }, { text: 'Waste −0.5%', good: true }]
        : [{ text: 'SPLH +$1.10', good: true }, { text: 'Speed −0.2m', good: true }, { text: 'Waste −0.2%', good: true }];
    case 'state':
      return w
        ? [{ text: `${store.name} SPLH +$3.10`, good: true }, { text: 'Speed −0.7m', good: true }, { text: 'Sales +7.5%', good: true }]
        : [{ text: `${store.name} SPLH +$0.90`, good: true }, { text: 'Speed −0.3m', good: true }, { text: 'Sales +2.4%', good: true }];
    case 'store': {
      const wasteGood = store.wastePct <= 3;
      return w
        ? [{ text: `SPLH ${store.splh >= TARGETS.splh ? '+$4.60' : '+$2.80'}`, good: true }, { text: 'Speed −0.8m', good: true }, { text: wasteGood ? 'Waste −0.6%' : 'Waste +0.3%', good: wasteGood }]
        : [{ text: `SPLH ${store.splh >= TARGETS.splh ? '+$1.40' : '+$0.80'}`, good: true }, { text: 'Speed −0.3m', good: true }, { text: wasteGood ? 'Waste −0.2%' : 'Waste +0.1%', good: wasteGood }];
    }
    case 'ops':
      return w
        ? [{ text: 'Speed −0.5m', good: true }, { text: '% under target +8pts', good: true }, { text: 'Accuracy +1.1%', good: true }]
        : [{ text: 'Speed −0.2m', good: true }, { text: '% under target +3pts', good: true }, { text: 'Accuracy +0.4%', good: true }];
    case 'finance':
      return w
        ? [{ text: 'Labour % −0.8', good: true }, { text: 'Waste $ −$640/wk', good: true }, { text: 'Delivery margin −0.5%', good: false }]
        : [{ text: 'Labour % −0.3', good: true }, { text: 'Waste $ −$180/wk', good: true }, { text: 'Delivery margin −0.2%', good: false }];
    case 'marketing':
      return w
        ? [{ text: 'Sales +6.8%', good: true }, { text: 'Combo attach +3pts', good: true }, { text: 'Delivery mix +5%', good: true }]
        : [{ text: 'Sales +2.1%', good: true }, { text: 'Combo attach +1pt', good: true }, { text: 'Delivery mix +2%', good: true }];
    case 'people':
      return w
        ? [{ text: 'SPLH +$3.40', good: true }, { text: 'Labour hours −150', good: true }, { text: 'Roster coverage +5%', good: true }]
        : [{ text: 'SPLH +$1.10', good: true }, { text: 'Labour hours −40', good: true }, { text: 'Roster coverage +2%', good: true }];
    case 'supervisor':
      return w
        ? [{ text: 'Shift speed −0.5m', good: true }, { text: 'Orders +180', good: true }, { text: 'On-pace +9%', good: true }]
        : [{ text: 'Shift speed −0.2m', good: true }, { text: 'Orders +12', good: true }, { text: 'On-pace +4%', good: true }];
    default:
      return [{ text: 'Speed −0.2m', good: true }, { text: 'SPLH +$1.10', good: true }];
  }
}

function buildView(role, a, store) {
  const state = STATE_OF[store.id];
  const money1 = (n) => '$' + (n / 1000).toFixed(1) + 'k';
  switch (role.id) {
    case 'owner':
      return {
        glow: '#34d399', scopeLabel: 'Network · 5 stores',
        tagline: 'Network health across every store, ranked by the margin that moves the P&L.',
        headline: `${money(a.netSales)}/wk network sales · ${a.best.name} leads`,
        ai: `Network SPLH is $${a.splh.toFixed(1)} vs $${TARGETS.splh} target; closing ${a.worst.name}'s gap is worth the most.`,
        glance: [
          { icon: DollarSign, label: 'Network weekly sales', value: money(a.netSales), sub: '5 stores', color: '#10b981', trend: [198, 202, 205, 208, 210, 214, 216, 218], delta: '+4.2%', deltaGood: true },
          { icon: Gauge, label: 'Network SPLH', value: `$${a.splh.toFixed(1)}`, sub: `target $${TARGETS.splh}`, color: '#6366f1', delta: `$${(a.splh - TARGETS.splh).toFixed(1)}`, deltaGood: a.splh >= TARGETS.splh },
          { icon: Timer, label: 'Avg speed of service', value: `${a.sos.toFixed(1)}m`, sub: `target ${TARGETS.sos}m`, color: '#3b82f6', delta: `${(a.sos - TARGETS.sos).toFixed(1)}m`, deltaGood: a.sos <= TARGETS.sos },
          { icon: Trash2, label: 'Waste', value: `${a.waste.toFixed(1)}%`, sub: 'target <2.5%', color: '#f59e0b', delta: `${(a.waste - 2.5).toFixed(1)}%`, deltaGood: a.waste <= 2.5 },
        ],
        focus: [
          { kicker: 'Biggest lever', icon: TrendingUp, color: '#10b981', title: `Lift ${a.worst.name} to network SPLH`, body: 'Cloning the benchmark dinner roster is the single biggest network win.', cta: 'Open store', store: a.worst },
          { kicker: 'Leaderboard', icon: Store, color: '#6366f1', title: 'Store league table', body: 'See every store ranked with benchmarks and AI analysis.', cta: 'View locations', tab: 'locations' },
          { kicker: 'AI playbook', icon: Sparkles, color: '#f59e0b', title: "Today's top moves", body: 'The prioritised, margin-ranked actions for the network.', cta: 'Open Corvana AI', tab: 'insights' },
        ],
      };
    case 'state':
      return {
        glow: '#38bdf8', scopeLabel: `${state} · ${store.name}`,
        tagline: `Your ${state} store performance and how it stacks up against the national network.`,
        headline: `${store.name}: $${store.splh.toFixed(1)} SPLH · ${store.sos.toFixed(1)}m speed`,
        ai: `${store.name} (${state}) is ${store.splh >= a.splh ? 'ahead of' : 'behind'} the national average on SPLH — focus the dinner day-part.`,
        glance: [
          { icon: DollarSign, label: `${state} weekly sales`, value: money(store.weeklySales), sub: store.name, color: '#0ea5e9', trend: store.salesTrend },
          { icon: Gauge, label: 'SPLH', value: `$${store.splh.toFixed(1)}`, sub: `network $${a.splh.toFixed(1)}`, color: '#6366f1', trend: store.splhTrend, delta: `$${(store.splh - a.splh).toFixed(1)}`, deltaGood: store.splh >= a.splh },
          { icon: Timer, label: 'Speed of service', value: `${store.sos.toFixed(1)}m`, sub: `target ${TARGETS.sos}m`, color: '#3b82f6', trend: store.sosTrend, delta: `${(store.sos - TARGETS.sos).toFixed(1)}m`, deltaGood: store.sos <= TARGETS.sos },
          { icon: TrendingUp, label: 'Order accuracy', value: `${store.accuracy.toFixed(1)}%`, sub: 'target 95%', color: '#10b981', delta: `${(store.accuracy - 95).toFixed(1)}%`, deltaGood: store.accuracy >= 95 },
        ],
        focus: [
          { kicker: 'Drill in', icon: Store, color: '#0ea5e9', title: `${store.name} full profile`, body: 'Day-part heatmaps, trends and the store-level actions.', cta: 'Open store', store },
          { kicker: 'Compare', icon: Map, color: '#6366f1', title: 'National league table', body: `See where ${state} ranks against every other state.`, cta: 'View locations', tab: 'locations' },
          { kicker: 'Labour', icon: Gauge, color: '#f59e0b', title: 'Labour & SPLH', body: `Fix ${state}'s roster shape at the peak day-parts.`, cta: 'Open labour', tab: 'labour' },
        ],
      };
    case 'area':
      return {
        glow: '#a78bfa', scopeLabel: 'Cluster · 5 stores',
        tagline: 'Your cluster at a glance — spot the outlier store and act before it drifts.',
        headline: `${a.best.name} best · ${a.worst.name} needs attention`,
        ai: `Your cluster spread on SPLH is $${(a.best.splh - a.worst.splh).toFixed(1)} — tighten ${a.worst.name} first.`,
        glance: [
          { icon: Gauge, label: 'Cluster SPLH', value: `$${a.splh.toFixed(1)}`, sub: `target $${TARGETS.splh}`, color: '#8b5cf6', delta: `$${(a.splh - TARGETS.splh).toFixed(1)}`, deltaGood: a.splh >= TARGETS.splh },
          { icon: Timer, label: 'Cluster speed', value: `${a.sos.toFixed(1)}m`, sub: `target ${TARGETS.sos}m`, color: '#3b82f6', delta: `${(a.sos - TARGETS.sos).toFixed(1)}m`, deltaGood: a.sos <= TARGETS.sos },
          { icon: Trash2, label: 'Cluster waste', value: `${a.waste.toFixed(1)}%`, sub: 'target <2.5%', color: '#f59e0b' },
          { icon: DollarSign, label: 'Cluster sales', value: money(a.netSales), sub: '5 stores', color: '#10b981', trend: [198, 205, 210, 214, 218], delta: '+4.2%', deltaGood: true },
        ],
        focus: [
          { kicker: 'Outlier', icon: Store, color: '#8b5cf6', title: `${a.worst.name} is the outlier`, body: 'Open its profile and assign the fix from the AI playbook.', cta: 'Open store', store: a.worst },
          { kicker: 'Speed', icon: Timer, color: '#3b82f6', title: 'Speed of service', body: 'Day-part heatmap across the cluster.', cta: 'Open speed', tab: 'speed' },
          { kicker: 'Coaching', icon: Radio, color: '#ef4444', title: 'Speed Coach', body: 'Live drive-through coaching during peaks.', cta: 'Open coach', tab: 'speedcoach' },
        ],
      };
    case 'store':
      return {
        glow: '#34d399', scopeLabel: `${store.name} · ${state}`,
        tagline: 'Your store today — the numbers you own and the actions waiting on you.',
        headline: `$${store.splh.toFixed(1)} SPLH · ${store.sos.toFixed(1)}m speed · ${store.wastePct.toFixed(1)}% waste`,
        ai: `Your dinner day-part is the weak spot (speed ${store.sosByDaypart[4].toFixed(1)}m). One extra runner 5–8pm lifts every number.`,
        glance: [
          { icon: Gauge, label: 'SPLH today', value: `$${store.splh.toFixed(1)}`, sub: `target $${TARGETS.splh}`, color: '#10b981', trend: store.splhTrend, delta: `$${(store.splh - TARGETS.splh).toFixed(1)}`, deltaGood: store.splh >= TARGETS.splh },
          { icon: Timer, label: 'Speed of service', value: `${store.sos.toFixed(1)}m`, sub: `target ${TARGETS.sos}m`, color: '#3b82f6', trend: store.sosTrend, delta: `${(store.sos - TARGETS.sos).toFixed(1)}m`, deltaGood: store.sos <= TARGETS.sos },
          { icon: Trash2, label: 'Waste', value: `${store.wastePct.toFixed(1)}%`, sub: 'target <2.5%', color: '#f59e0b', delta: `${(store.wastePct - 2.5).toFixed(1)}%`, deltaGood: store.wastePct <= 2.5 },
          { icon: DollarSign, label: 'Weekly sales', value: money(store.weeklySales), sub: store.name, color: '#6366f1', trend: store.salesTrend },
        ],
        focus: [
          { kicker: 'Your actions', icon: Sparkles, color: '#10b981', title: 'Actions waiting on you', body: 'Open & overdue store actions from Corvana AI.', cta: 'Open Corvana AI', tab: 'insights' },
          { kicker: 'Roster', icon: Users, color: '#6366f1', title: 'Roster ahead of dinner', body: 'Day-part forecast and recommended crew.', cta: 'Open sales', tab: 'sales' },
          { kicker: 'Live', icon: Radio, color: '#ef4444', title: 'Speed Coach', body: 'Live coaching for your drive-through right now.', cta: 'Open coach', tab: 'speedcoach' },
        ],
      };
    case 'ops':
      return {
        glow: '#60a5fa',
        tagline: 'Throughput and consistency — speed, accuracy and where the peaks choke.',
        headline: `${a.sos.toFixed(1)}m network speed · ${a.acc.toFixed(1)}% accuracy`,
        ai: 'Reds cluster on lunch & dinner across all stores — this is a roster-shape problem, not a kitchen one.',
        glance: [
          { icon: Timer, label: 'Speed of service', value: `${a.sos.toFixed(1)}m`, sub: `target ${TARGETS.sos}m`, color: '#3b82f6', trend: [4.1, 4.0, 3.9, 3.9, 3.8, 3.7, 3.6], delta: `${(a.sos - TARGETS.sos).toFixed(1)}m`, deltaGood: a.sos <= TARGETS.sos },
          { icon: Gauge, label: 'Stores under target', value: '33%', sub: '1 of 3', color: '#f59e0b' },
          { icon: TrendingUp, label: 'Order accuracy', value: `${a.acc.toFixed(1)}%`, sub: 'target 95%', color: '#10b981', delta: `${(a.acc - 95).toFixed(1)}%`, deltaGood: a.acc >= 95 },
          { icon: Bike, label: 'Peak drive-through', value: '6.1m', sub: `${a.worst.name} dinner`, color: '#ef4444' },
        ],
        focus: [
          { kicker: 'Bottleneck', icon: Timer, color: '#3b82f6', title: 'Speed of service heatmap', body: 'See exactly which day-parts choke, by store.', cta: 'Open speed', tab: 'speed' },
          { kicker: 'Live', icon: Radio, color: '#ef4444', title: 'Speed Coach', body: 'Live pacing + crew nudges during peaks.', cta: 'Open coach', tab: 'speedcoach' },
          { kicker: 'Waste', icon: Trash2, color: '#f59e0b', title: 'Waste & voids', body: 'Remakes drive both slow speed and lost margin.', cta: 'Open waste', tab: 'waste' },
        ],
      };
    case 'finance':
      return {
        glow: '#f472b6',
        tagline: 'The P&L view — labour, waste and aggregator fees eating your margin.',
        headline: `Labour ${a.labour.toFixed(1)}% · waste ${a.waste.toFixed(1)}% · delivery ${a.delivery.toFixed(0)}%`,
        ai: `Labour is ${a.labour.toFixed(1)}% and waste ${a.waste.toFixed(1)}% — together ~$${(a.netSales * 0.01).toFixed(0)}/wk of recoverable margin.`,
        glance: [
          { icon: DollarSign, label: 'Network sales', value: money(a.netSales), sub: 'weekly', color: '#10b981', trend: [198, 205, 210, 214, 218], delta: '+4.2%', deltaGood: true },
          { icon: Gauge, label: 'Labour cost %', value: `${a.labour.toFixed(1)}%`, sub: 'target <28%', color: '#6366f1', delta: `${(a.labour - 28).toFixed(1)}%`, deltaGood: a.labour <= 28 },
          { icon: Trash2, label: 'Waste $ / wk', value: money(6520), sub: 'est. margin lost', color: '#f59e0b' },
          { icon: Bike, label: 'Aggregator fees', value: money(4180), sub: `~${a.delivery.toFixed(0)}% of sales`, color: '#ef4444' },
        ],
        focus: [
          { kicker: 'Labour', icon: Gauge, color: '#6366f1', title: 'Labour & SPLH', body: 'Where labour % runs hot without lifting sales.', cta: 'Open labour', tab: 'labour' },
          { kicker: 'Waste', icon: Trash2, color: '#f59e0b', title: 'Waste & voids', body: 'Dollar impact by store and reason.', cta: 'Open waste', tab: 'waste' },
          { kicker: 'Delivery', icon: Bike, color: '#ef4444', title: 'Delivery margins', body: 'Aggregator mix and what it costs per order.', cta: 'Open delivery', tab: 'delivery' },
        ],
      };
    case 'marketing':
      return {
        glow: '#fb923c',
        tagline: 'Sales mix, attach rates and delivery demand — where to push the next promo.',
        headline: `$17.27 avg check · 55% combo attach`,
        ai: 'Combo attachment sits at 55% — a scripted POS upsell is the fastest, highest-margin lift.',
        glance: [
          { icon: DollarSign, label: 'Average check', value: '$17.27', sub: 'per order', color: '#fb923c', delta: '+$0.40', deltaGood: true },
          { icon: TrendingUp, label: 'Combo attach', value: '55%', sub: 'upsell rate', color: '#10b981', delta: '+3%', deltaGood: true },
          { icon: Bike, label: 'Delivery mix', value: `${a.delivery.toFixed(0)}%`, sub: 'of sales', color: '#ef4444' },
          { icon: DollarSign, label: 'Weekly sales', value: money(a.netSales), sub: '5 stores', color: '#6366f1', trend: [198, 205, 210, 214, 218] },
        ],
        focus: [
          { kicker: 'Mix', icon: DollarSign, color: '#fb923c', title: 'Sales & menu mix', body: 'Top items, day-part demand and attach rates.', cta: 'Open sales', tab: 'sales' },
          { kicker: 'Delivery', icon: Bike, color: '#ef4444', title: 'Delivery & aggregators', body: 'Uber Eats vs DoorDash demand and ratings.', cta: 'Open delivery', tab: 'delivery' },
          { kicker: 'AI', icon: Sparkles, color: '#10b981', title: 'Upsell opportunity', body: 'Corvana AI’s highest-margin promo moves.', cta: 'Open Corvana AI', tab: 'insights' },
        ],
      };
    case 'people':
      return {
        glow: '#22d3ee',
        tagline: 'Staffing and roster coverage — get the right crew on at the right hour.',
        headline: `${a.labour.toFixed(1)}% labour · dinner peak needs cover`,
        ai: 'Labour spikes at dinner in every store yet SPLH still lags — shift crew from the quiet arvo into the peak.',
        glance: [
          { icon: Users, label: 'Active staff', value: '42', sub: 'across 5 stores', color: '#06b6d4' },
          { icon: Gauge, label: 'Labour cost %', value: `${a.labour.toFixed(1)}%`, sub: 'target <28%', color: '#6366f1', delta: `${(a.labour - 28).toFixed(1)}%`, deltaGood: a.labour <= 28 },
          { icon: DollarSign, label: 'SPLH', value: `$${a.splh.toFixed(1)}`, sub: `target $${TARGETS.splh}`, color: '#10b981', delta: `$${(a.splh - TARGETS.splh).toFixed(1)}`, deltaGood: a.splh >= TARGETS.splh },
          { icon: Timer, label: 'Peak coverage gap', value: 'Dinner', sub: `${a.worst.name} 5–8pm`, color: '#ef4444' },
        ],
        focus: [
          { kicker: 'Roster', icon: Gauge, color: '#6366f1', title: 'Labour & SPLH', body: 'Labour % heatmap by day-part and store.', cta: 'Open labour', tab: 'labour' },
          { kicker: 'Forecast', icon: Users, color: '#06b6d4', title: 'Roster to the forecast', body: 'Recommended crew per day-part for tomorrow.', cta: 'Open sales', tab: 'sales' },
          { kicker: 'Live', icon: Radio, color: '#ef4444', title: 'Speed Coach', body: 'See if the current shift is coping with the peak.', cta: 'Open coach', tab: 'speedcoach' },
        ],
      };
    case 'supervisor':
    default:
      return {
        glow: '#f87171', scopeLabel: `${store.name} · this shift`,
        tagline: 'Your shift, right now — live pace and the jobs to knock out before close.',
        headline: `${store.name} live: ${store.sos.toFixed(1)}m speed at dinner`,
        ai: `You're ${store.sos <= TARGETS.sos ? 'on pace' : 'over target'} — keep the second runner on the window until the 5–8pm rush clears.`,
        glance: [
          { icon: Radio, label: 'Live speed', value: `${store.sos.toFixed(1)}m`, sub: `target ${TARGETS.sos}m`, color: '#ef4444', delta: `${(store.sos - TARGETS.sos).toFixed(1)}m`, deltaGood: store.sos <= TARGETS.sos },
          { icon: TrendingUp, label: 'Accuracy', value: `${store.accuracy.toFixed(1)}%`, sub: 'this shift', color: '#10b981' },
          { icon: Users, label: 'Crew on', value: '8', sub: 'dinner peak', color: '#06b6d4' },
          { icon: Trash2, label: 'Voids', value: `${store.voidPct.toFixed(1)}%`, sub: 'watch remakes', color: '#f59e0b' },
        ],
        focus: [
          { kicker: 'Live', icon: Radio, color: '#ef4444', title: 'Speed Coach', body: 'Live pacing and crew nudges for the window.', cta: 'Open coach', tab: 'speedcoach' },
          { kicker: 'Jobs', icon: Sparkles, color: '#10b981', title: 'Shift actions', body: 'What’s open and overdue for this store.', cta: 'Open Corvana AI', tab: 'insights' },
          { kicker: 'Store', icon: Store, color: '#6366f1', title: `${store.name} profile`, body: 'Full day-part detail for your store.', cta: 'Open store', store },
        ],
      };
  }
}
